import {
    getSparkflowGlobalContext,
    MilestoneAgentName,
    MilestoneStatus,
    setSparkflowGlobalContext,
} from '@thoughtspot/blink-context';
import { logger } from '@thoughtspot/client-state-util/esm/utils';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import {
    listenToAllOnboardingEvents,
    OnboardingEvent,
    OnboardingEventId,
    OnboardingEventPayload,
    sendOnboardingEvent,
} from '@thoughtspot/onboarding-event-service';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import {
    CallBackProps,
    LIFECYCLE,
    STATUS,
    Tour,
} from '@thoughtspot/radiant-react/widgets/tour';
import {
    getOnboardingLocationSelected,
    getUserDisplayName,
    getUserPersona,
} from '@thoughtspot/session-service';
import { MilestoneModal } from '@thoughtspot/sparkflow';
import { isBoolean } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    useGetChecklistItems,
    useMarkChecklistItems,
} from '/@services/trial/trial-service';
import {
    ChecklistItem,
    ChecklistSubtask,
    ChecklistTaskNavigationType,
    ChecklistTaskType,
} from './checklist-modal/checklist-modal.types';
import {
    delay,
    EXPLORE_INSIGHTS_USE_ENGAGE_EVENT_URL,
    getChecklistTourChain,
    LAYOUT_SETTLE_DELAY_MS,
    performSequentialActions,
    performTourAction,
    TourChainStep,
    WAIT_FOR_ELEMENT_SAFETY_TIMEOUT_MS,
    waitForElement,
    waitForElementRemoval,
} from './checklist-tour/checklist-tour-steps.util';
import { getMilestoneDecision, MilestoneDecision } from './milestone.util';
import { TrialInfoProps } from './trial-info';
import {
    CHECKLIST_UPDATE_DELAY_MS,
    ChecklistTaskKey,
    findSectionByTaskId,
    formatDate,
    getBaseUrlForTaskType,
    getCompletedToastTitle,
    getFirstIncompleteSectionIndex,
    getInitialTaskExpandedState,
    getNestedAccordionDataTestId,
    getSectionIndexFromId,
    getSectionIndexFromSectionId,
    getSparkflowOnboardingSelections,
    getTaskKeyForPersona,
    isSectionComplete,
} from './trial-info.util';
import {
    checklistClosed,
    checklistErrorShown,
    checklistFooterCtaClicked,
    checklistGuideClosed,
    checklistGuideCtaClicked,
    checklistGuideLoaded,
    checklistOpened,
    checklistSectionCompleted,
    checklistSectionSelected,
    checklistTaskCompleted,
    checklistTaskCtaClicked,
    checklistTaskSelected,
    FooterCtaType,
    LoadType,
    milestoneCtaClicked,
    milestoneModalClosed,
    milestoneModalLoaded,
} from './trial-info-metrics';
import { TrialInfoSparkflow } from './trial-info-sparkflow';
import {
    MilestoneCtaActionType,
    TOUR_CARD_WIDTH,
    TOUR_CARD_Z_INDEX,
} from './trial-info-sparkflow.constants';

export interface TrialInfoSparkflowContainerProps extends TrialInfoProps {
    pathname: string;
    routeHash: string;
    goToUrl: (url: string, inNewTab?: boolean) => void;
    createPinboard: any;
}

type ActiveTourContext = {
    taskId: string;
    loadType: LoadType;
};

export const TrialInfoSparkflowContainer: React.FC<TrialInfoSparkflowContainerProps> = ({
    createPinboard,
    pathname,
    routeHash,
    goToUrl,
    isOnboardingMTServicesEnabled,
    ...trialInfoProps
}) => {
    const { t } = useTranslation();
    const {
        spotbuildGenResponse,
        savedRole,
        checklistRefreshRequested,
        hideSparkflowBanner,
        lastOpenedChecklistSectionId,
        spotterConversationResetKey,
        milestone,
    } = getSparkflowGlobalContext();
    const {
        requestChecklistRefresh,
        updateSparkflowGlobalContext,
        updateMilestone,
    } = setSparkflowGlobalContext();
    const [pendingEvents, setPendingEvents] = useState<
        OnboardingEvent<OnboardingEventPayload>[]
    >([]);
    const checkedItemsRef = useRef<Set<string>>(new Set());
    const validTaskIdsRef = useRef<Set<string>>(new Set());

    const selectedPersona = getUserPersona();
    const selectedPersonaTaskKey = getTaskKeyForPersona(selectedPersona);
    const selectedAgent = getOnboardingLocationSelected();

    const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
    const [checklistOpen, setChecklistOpen] = useState(false);
    const [isToastVisible, setIsToastVisible] = useState(false);
    // Tracks which section was just completed — defaults to the user's persona
    // section, updated to the actual completed section when toast fires.
    const [completedSectionKey, setCompletedSectionKey] = useState<
        ChecklistTaskKey
    >(selectedPersonaTaskKey);

    const [taskExpandedState, setTaskExpandedState] = useState<
        Record<string, number[]>
    >({});
    const [
        headerExpandedItemsIndexes,
        setHeaderExpandedItemsIndexes,
    ] = useState<number[]>([0]);

    const [scrollToElementId, setScrollToElementId] = useState<string | null>(
        null,
    );

    const [lastCompletedTaskId, setLastCompletedTaskId] = useState<
        string | null
    >(null);

    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    const [activeTourChain, setActiveTourChain] = useState<
        TourChainStep[] | null
    >(null);
    const [tourChainIndex, setTourChainIndex] = useState(0);
    const [isTourReady, setIsTourReady] = useState(false);
    const [isModalOnTourPageOpen, setIsModalOnTourPageOpen] = useState(false);
    const tourInstanceRef = useRef(0);
    const tourAbortRef = useRef<AbortController | null>(null);
    const agentTourPausedRef = useRef<number | null>(null);
    /* Refs instead of state — writing these must not trigger a re-render. */
    const activeTourContextRef = useRef<ActiveTourContext | null>(null);
    const shownMilestoneStartedRef = useRef(false);
    const shownMilestoneFinishedRef = useRef(false);
    const isFindYourWayTourRef = useRef(false);

    const {
        markChecklistItems,
        error: markChecklistError,
    } = useMarkChecklistItems();
    const {
        data: sparkflowInsightsData,
        loading: sparkflowInsightsLoading,
        error: checklistItemsError,
        refetch: refetchChecklistItems,
    } = useGetChecklistItems();

    /**
     * @effect - useEffect to refresh the checklist items when the checklistRefreshRequested is true
     * @description - Refetch the checklist items when the checklistRefreshRequested is true
     */
    useEffect(() => {
        if (checklistRefreshRequested) {
            requestChecklistRefresh({ checklistRefreshRequested: false }); // reset the checklistRefreshRequested to false in the context
            updateSparkflowGlobalContext({ lastOpenedChecklistSectionId: '' }); // clear stale section from previous persona session
            refetchChecklistItems(); // API call to refetch the checklist items
        }
    }, [checklistRefreshRequested]);

    const checklistOpenContextRef = useRef<{
        loadType: LoadType;
        sectionOpened: string;
        taskOpened: string;
        lastSectionSelected: string;
        lastTaskSelected: string;
    }>({
        loadType: LoadType.MANUAL,
        sectionOpened: '',
        taskOpened: '',
        lastSectionSelected: '',
        lastTaskSelected: '',
    });

    const [bannerAlertMessage, setBannerAlertMessage] = useState<{
        isVisible: boolean;
        errorMessage: string;
    }>({
        isVisible: false,
        errorMessage: '',
    });

    const clearTourChain = (): void => {
        tourAbortRef.current?.abort();
        agentTourPausedRef.current = null;
        activeTourContextRef.current = null;
        isFindYourWayTourRef.current = false;
        setActiveTourChain(null);
        setTourChainIndex(0);
        setIsTourReady(false);
    };

    const trackGuideLoaded = (taskId: string, loadType: LoadType): void => {
        activeTourContextRef.current = { taskId, loadType };
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        const ctx = checklistOpenContextRef.current;
        checklistGuideLoaded(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            sectionOpened: ctx.sectionOpened,
            sectionSelected:
                findSectionByTaskId(sparkflowInsightsData, taskId)?.id ?? '',
            taskOpened: ctx.taskOpened,
            taskSelected: taskId,
            loadType,
        });
    };

    const trackGuideCtaClicked = (taskId: string, loadType: LoadType): void => {
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        const ctx = checklistOpenContextRef.current;
        checklistGuideCtaClicked(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            sectionOpened: ctx.sectionOpened,
            sectionSelected:
                findSectionByTaskId(sparkflowInsightsData, taskId)?.id ?? '',
            taskOpened: ctx.taskOpened,
            taskSelected: taskId,
            loadType,
        });
    };

    const trackGuideClosed = (taskId: string, loadType: LoadType): void => {
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        const ctx = checklistOpenContextRef.current;
        checklistGuideClosed(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            sectionOpened: ctx.sectionOpened,
            sectionSelected:
                findSectionByTaskId(sparkflowInsightsData, taskId)?.id ?? '',
            taskOpened: ctx.taskOpened,
            taskSelected: taskId,
            loadType,
        });
    };

    const trackMilestoneModalLoaded = (
        agentName: string,
        status: MilestoneStatus,
    ): void => {
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        milestoneModalLoaded(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            agentCardSelected: agentName,
            milestoneReached: status,
        });
    };

    const trackMilestoneModalClosed = (
        agentName: string,
        status: MilestoneStatus,
    ): void => {
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        milestoneModalClosed(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            agentCardSelected: agentName,
            milestoneReached: status,
        });
    };

    const trackMilestoneCtaClicked = (
        agentName: string,
        status: MilestoneStatus,
        actionType: MilestoneCtaActionType,
    ): void => {
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        milestoneCtaClicked(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            agentCardSelected: agentName,
            milestoneReached: status,
            ctaClicked: actionType,
        });
    };

    const trackChecklistFooterCtaClicked = (
        ctaClicked: FooterCtaType,
    ): void => {
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        checklistFooterCtaClicked(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            agentCardSelected: selectedAgent,
            ctaClicked,
        });
    };

    const dismissAgentTourStep = (advanceDirectly = false) => {
        if (!activeTourChain) return;
        const currentStep = activeTourChain[tourChainIndex];
        const isDismissAllowed =
            currentStep?.dismissOnAfterClick ||
            currentStep?.path ||
            currentStep?.dismissOnAction;

        if (!isDismissAllowed) return;
        tourInstanceRef.current += 1;
        setIsTourReady(false);
        const nextIndex = tourChainIndex + 1;
        if (nextIndex < activeTourChain.length) {
            if (advanceDirectly) {
                setTourChainIndex(nextIndex);
            } else {
                agentTourPausedRef.current = nextIndex;
            }
        } else {
            clearTourChain();
        }
    };

    const checkTourDismiss = () => {
        if (!isTourReady) return;
        const stepPath = activeTourChain?.[tourChainIndex]?.path;
        if (!stepPath) return;
        const hash = routeHash.replace(/^#/, '');
        const isOnPath = stepPath.some(p =>
            p === '/' ? hash === '/' || hash === '' : hash.startsWith(p),
        );
        if (!isOnPath) {
            agentTourPausedRef.current = null;
            dismissAgentTourStep(true);
        }
    };

    useEffect(() => {
        checkTourDismiss();
    }, [routeHash]);

    useEffect(() => {
        const apiError = checklistItemsError || markChecklistError;
        if (!apiError) return;

        setBannerAlertMessage({
            isVisible: true,
            errorMessage: apiError?.message || t('error.validation.default'),
        });

        const selections = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        const ctx = checklistOpenContextRef.current;
        checklistErrorShown(trialInfoProps.collectEvent, {
            selectedFunction: selections.selectedFunction,
            selectedIndustry: selections.selectedIndustry,
            sectionOpened: ctx.sectionOpened,
            sectionSelected: ctx.lastSectionSelected,
            taskOpened: ctx.taskOpened,
            taskSelected: ctx.lastTaskSelected,
            loadType: ctx.loadType,
            errorType: apiError?.message || 'Checklist API error',
        });
    }, [checklistItemsError, markChecklistError]);

    const handleSectionHeaderClick = (expandedIndexes: number[]) => {
        if (expandedIndexes.length === 0) {
            setHeaderExpandedItemsIndexes([]);
            return;
        }
        const newIndex = expandedIndexes.filter(
            index => !headerExpandedItemsIndexes.includes(index),
        );
        if (newIndex.length > 0) {
            setHeaderExpandedItemsIndexes(newIndex);

            const sectionIndex = newIndex[0];
            const sectionId = sparkflowInsightsData?.[sectionIndex]?.id;
            if (!sectionId) return;
            checklistOpenContextRef.current.lastSectionSelected = sectionId;
            updateSparkflowGlobalContext({
                lastOpenedChecklistSectionId: sectionId,
            });
            const checkedSnapshot = checkedItemsRef.current;
            const { firstIncompletePerSection } = getInitialTaskExpandedState(
                sparkflowInsightsData,
                checkedSnapshot,
            );
            const sectionData = sparkflowInsightsData?.[sectionIndex];
            checklistOpenContextRef.current.lastTaskSelected =
                firstIncompletePerSection[sectionId] ||
                sectionData?.tasks?.[0]?.id ||
                '';
            const {
                selectedFunction,
                selectedIndustry,
            } = getSparkflowOnboardingSelections(
                isOnboardingMTServicesEnabled,
                savedRole,
            );
            checklistSectionSelected(trialInfoProps.collectEvent, {
                selectedFunction,
                selectedIndustry,
                sectionOpened: checklistOpenContextRef.current.sectionOpened,
                sectionSelected: sectionId,
                taskOpened: checklistOpenContextRef.current.taskOpened,
            });
        }
    };

    const handleTaskHeaderClick = (
        sectionId: string,
        expandedIndexes: number[],
    ) => {
        const newlyExpandedIndexes = expandedIndexes.filter(
            index => !taskExpandedState[sectionId].includes(index),
        );

        setTaskExpandedState(prev => {
            const updated = {
                ...prev,
                [sectionId]: newlyExpandedIndexes,
            };
            return updated;
        });

        if (newlyExpandedIndexes.length > 0) {
            const section = sparkflowInsightsData?.find(
                (item: ChecklistItem) => item.id === sectionId,
            );
            const task = section?.tasks?.[newlyExpandedIndexes[0]];
            if (task) {
                checklistOpenContextRef.current.lastTaskSelected = task.id;
                const {
                    selectedFunction,
                    selectedIndustry,
                } = getSparkflowOnboardingSelections(
                    isOnboardingMTServicesEnabled,
                    savedRole,
                );
                checklistTaskSelected(trialInfoProps.collectEvent, {
                    selectedFunction,
                    selectedIndustry,
                    sectionOpened:
                        checklistOpenContextRef.current.sectionOpened,
                    sectionSelected: sectionId,
                    taskOpened: checklistOpenContextRef.current.taskOpened,
                    taskSelected: task.id,
                    loadType: checklistOpenContextRef.current.loadType,
                });
            }
        }
    };

    useEffect(() => {
        if (!sparkflowInsightsData) return;
        const {
            taskExpandedState: newExpandedState,
        } = getInitialTaskExpandedState(sparkflowInsightsData, checkedItems);
        setTaskExpandedState(newExpandedState);
    }, [sparkflowInsightsData, checkedItems]);

    useEffect(() => {
        const completedIds =
            sparkflowInsightsData?.reduce(
                (acc: string[], item: ChecklistItem) => {
                    item.tasks?.forEach((task: ChecklistTaskType) => {
                        validTaskIdsRef.current.add(task.id);
                        if (task?.completed) {
                            acc.push(task.id);
                        }
                        task.sub_tasks?.forEach((subtask: ChecklistSubtask) => {
                            validTaskIdsRef.current.add(subtask.id);
                            if (subtask?.completed) {
                                acc.push(subtask.id);
                            }
                        });
                    });
                    return acc;
                },
                [],
            ) ?? [];

        setCheckedItems(prev => {
            const newSet = new Set([...prev, ...completedIds]);
            checkedItemsRef.current = newSet;
            return newSet;
        });
    }, [sparkflowInsightsData]);

    useEffect(() => {
        if (!scrollToElementId) return;

        // DOM is updated
        requestAnimationFrame(() => {
            // Element position updated
            requestAnimationFrame(() => {
                // As we have multiple accordions, using multiple refs
                // is not a good approach, using querySelector instead of ref
                const dataTestId = getNestedAccordionDataTestId(
                    scrollToElementId,
                );
                const selector = `[data-testid="${dataTestId}"]`;
                const checkedElement = document.querySelector(selector);

                if (checkedElement) {
                    checkedElement.scrollIntoView({
                        behavior: 'smooth',
                    });
                }

                // Reset scroll trigger after attempting scroll
                setScrollToElementId(null);
            });
        });
    }, [scrollToElementId, headerExpandedItemsIndexes]);

    const getAgentTourChain = (
        eventId: string,
        includeCloseStep = true,
    ): TourChainStep[] | null => {
        const tourChain = getChecklistTourChain(eventId, t);
        if (!tourChain?.some(step => step.dismissOnAfterClick)) return null;
        if (includeCloseStep) return tourChain;
        return tourChain.filter(step => !step.showOnTourClose);
    };

    const startAgentTour = (eventId: string) => {
        if (agentTourPausedRef.current !== null) return;
        const tourChain = getAgentTourChain(eventId);
        if (!tourChain?.length) return;
        if (eventId === OnboardingEventId.SPOTTER_VIZ_BUILD_FIRST_DASHBOARD) {
            agentTourPausedRef.current = 0;
        } else {
            agentTourPausedRef.current = 1;
        }
        tourInstanceRef.current += 1;
        setTourChainIndex(0);
        setActiveTourChain(tourChain);
        trackGuideLoaded(eventId, LoadType.AUTO);
    };

    // Process an onboarding event (either immediately or from queue)
    const processEvent = useCallback(
        (event: OnboardingEvent<OnboardingEventPayload>) => {
            if (!event || !event?.key) {
                return;
            }

            if (event.key === OnboardingEventId.TOUR_GUIDE_VISIBLE) {
                const tourGuideVisible = event.payload?.tourGuideVisible;
                if (isBoolean(tourGuideVisible)) {
                    setIsModalOnTourPageOpen(tourGuideVisible);
                }
                return;
            }

            if (event.payload?.isAgentTour) {
                startAgentTour(event.key);
                return;
            }

            if (event.payload?.resumeAgentTour) {
                if (
                    agentTourPausedRef.current === null &&
                    event.payload?.resumeAgentTour > tourChainIndex
                ) {
                    agentTourPausedRef.current = event.payload?.resumeAgentTour;
                }
            }

            if (agentTourPausedRef.current > 0) {
                const nextIndex = agentTourPausedRef.current;
                agentTourPausedRef.current = null;
                tourInstanceRef.current += 1;
                setIsTourReady(false);
                setTourChainIndex(nextIndex);
            }

            // skip marking if we are resuming the tour
            if (event.payload?.resumeAgentTour) {
                return;
            }

            if (checkedItemsRef.current.has(event?.key)) {
                return;
            }

            if (!validTaskIdsRef.current.has(event.key)) {
                return;
            }

            if (event && event?.key) {
                setTimeout(() => {
                    const id = event?.key;
                    const taskSection = findSectionByTaskId(
                        sparkflowInsightsData,
                        id,
                    );
                    setLastCompletedTaskId(id);

                    const updatedCheckedItems = new Set(
                        checkedItemsRef.current,
                    );
                    updatedCheckedItems.add(id);
                    checkedItemsRef.current = updatedCheckedItems;
                    setCheckedItems(updatedCheckedItems);

                    const wasSectionCompleted = !!(
                        taskSection &&
                        isSectionComplete(taskSection, updatedCheckedItems)
                    );

                    const {
                        showMilestoneModal,
                        milestoneConfig,
                        milestoneStatus,
                    }: MilestoneDecision = getMilestoneDecision(
                        wasSectionCompleted,
                        sparkflowInsightsData,
                        updatedCheckedItems,
                        shownMilestoneStartedRef.current,
                        shownMilestoneFinishedRef.current,
                    );

                    setChecklistOpen(!showMilestoneModal);
                    if (showMilestoneModal && milestoneConfig) {
                        if (milestoneStatus === MilestoneStatus.FINISHED) {
                            shownMilestoneFinishedRef.current = true;
                        } else {
                            shownMilestoneStartedRef.current = true;
                        }
                        updateMilestone({
                            isModalOpen: true,
                            agentName: milestoneConfig.agentName,
                            status: milestoneStatus,
                        });
                        trackMilestoneModalLoaded(
                            milestoneConfig.agentName,
                            milestoneStatus,
                        );
                    }

                    if (wasSectionCompleted && !showMilestoneModal) {
                        setCompletedSectionKey(
                            taskSection?.id as ChecklistTaskKey,
                        );
                        setIsToastVisible(true);
                    }

                    const checkedSnapshot = checkedItemsRef.current;
                    const completedSectionId = taskSection?.id || '';
                    const {
                        firstIncompletePerSection,
                    } = getInitialTaskExpandedState(
                        sparkflowInsightsData,
                        checkedSnapshot,
                    );
                    const firstIncompleteInSection =
                        firstIncompletePerSection[completedSectionId] || '';

                    let sectionOpened: string;
                    let taskOpened: string;

                    if (firstIncompleteInSection) {
                        sectionOpened = completedSectionId;
                        taskOpened = firstIncompleteInSection;
                    } else {
                        const result = getFirstIncompleteSectionIndex(
                            sparkflowInsightsData,
                            checkedSnapshot,
                        );
                        sectionOpened = result.sectionId;
                        taskOpened = result.taskId;
                    }

                    markChecklistItems([id]);

                    const sectionIndex = getSectionIndexFromId(
                        sparkflowInsightsData,
                        id,
                    );
                    setHeaderExpandedItemsIndexes([sectionIndex]);
                    setScrollToElementId(id);

                    checklistOpenContextRef.current = {
                        loadType: LoadType.AUTO,
                        sectionOpened,
                        taskOpened,
                        lastSectionSelected: sectionOpened,
                        lastTaskSelected: taskOpened,
                    };

                    const onboardingSelections = getSparkflowOnboardingSelections(
                        isOnboardingMTServicesEnabled,
                        savedRole,
                    );
                    checklistOpened(trialInfoProps.collectEvent, {
                        selectedFunction: onboardingSelections.selectedFunction,
                        selectedIndustry: onboardingSelections.selectedIndustry,
                        sectionOpened,
                        taskOpened,
                        loadType: LoadType.AUTO,
                    });

                    checklistTaskCompleted(trialInfoProps.collectEvent, {
                        selectedFunction: onboardingSelections.selectedFunction,
                        selectedIndustry: onboardingSelections.selectedIndustry,
                        agentCardSelected: selectedAgent,
                        sectionOpened,
                        sectionSelected: taskSection?.id,
                        taskOpened,
                        taskSelected: id,
                        loadType: LoadType.AUTO,
                    });

                    if (wasSectionCompleted) {
                        checklistSectionCompleted(trialInfoProps.collectEvent, {
                            selectedFunction:
                                onboardingSelections.selectedFunction,
                            selectedIndustry:
                                onboardingSelections.selectedIndustry,
                            agentCardSelected: selectedAgent,
                            sectionOpened,
                            sectionSelected: completedSectionId,
                            taskOpened,
                            taskSelected: id,
                            loadType: LoadType.AUTO,
                        });
                    }
                }, CHECKLIST_UPDATE_DELAY_MS);
            }
        },
        [markChecklistItems, sparkflowInsightsData, trialInfoProps],
    );

    useEffect(() => {
        const subscription = listenToAllOnboardingEvents().subscribe({
            next: (event: OnboardingEvent<OnboardingEventPayload>) => {
                setPendingEvents(prev => [...prev, event]);
            },
            error: (error: Error) => {
                logger.error('Error in event listener:', error);
            },
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!sparkflowInsightsLoading && pendingEvents.length > 0) {
            pendingEvents.forEach(processEvent);
            setPendingEvents([]);
        }
    }, [sparkflowInsightsLoading, pendingEvents, processEvent]);

    const listenForAgentTourDismiss = (
        selector: string,
        signal: AbortSignal,
    ) => {
        const targetEl = document.querySelector(selector);
        targetEl?.addEventListener('click', () => dismissAgentTourStep(), {
            signal,
        });
        document.addEventListener(
            'keydown',
            (e: KeyboardEvent) => {
                if (e.key === 'Enter') dismissAgentTourStep();
            },
            { signal },
        );
    };

    useEffect(() => {
        if (!activeTourChain) {
            setIsTourReady(false);
            return undefined;
        }

        tourAbortRef.current?.abort();
        const controller = new AbortController();
        tourAbortRef.current = controller;
        const { signal } = controller;
        const timeout = WAIT_FOR_ELEMENT_SAFETY_TIMEOUT_MS;

        const currentStep = activeTourChain[tourChainIndex];
        const firstTarget =
            currentStep?.readyTarget ?? currentStep?.steps[0]?.target;

        const setup = async () => {
            const hadBeforeActions = !!currentStep?.beforeActions?.length;

            if (hadBeforeActions) {
                const success = await performSequentialActions(
                    currentStep.beforeActions,
                    signal,
                    timeout,
                );
                if (signal.aborted || !success) {
                    return;
                }
            } else if (currentStep?.beforeAction) {
                const success = await performTourAction(
                    currentStep.beforeAction,
                    signal,
                    timeout,
                );
                if (signal.aborted || !success) {
                    return;
                }
            }

            if (typeof firstTarget === 'string') {
                if (currentStep?.waitForTargetRemoval) {
                    await waitForElementRemoval(firstTarget, signal, timeout);
                    if (signal.aborted) return;
                }
                const el = await waitForElement(firstTarget, signal, timeout);

                if (signal.aborted) {
                    return;
                }
                if (
                    (hadBeforeActions ||
                        currentStep?.dismissOnAfterClick ||
                        !!currentStep?.readyTarget) &&
                    el
                ) {
                    await delay(LAYOUT_SETTLE_DELAY_MS);
                }
                if (currentStep?.condition && !currentStep.condition()) {
                    if (currentStep.dismissOnAfterClick) {
                        clearTourChain();
                    } else {
                        const nextIdx = tourChainIndex + 1;
                        if (nextIdx < activeTourChain.length) {
                            setTourChainIndex(nextIdx);
                        } else {
                            clearTourChain();
                        }
                    }
                    return;
                }
                if (signal.aborted) return;
                if (el) {
                    setIsTourReady(true);
                } else {
                    clearTourChain();
                }
            } else if (firstTarget) {
                setIsTourReady(true);
            }

            if (signal.aborted) return;
            if (currentStep?.dismissOnAfterClick) {
                const dismissSelector = currentStep.afterActions?.length
                    ? currentStep.afterActions[0].selector
                    : currentStep.afterAction?.selector;
                if (dismissSelector) {
                    listenForAgentTourDismiss(dismissSelector, signal);
                }
            }
            if (currentStep?.dismissOnAction) {
                listenForAgentTourDismiss(
                    currentStep.dismissOnAction.selector,
                    signal,
                );
            }
        };

        setup().catch(error => {
            if (error?.name === 'AbortError' || signal.aborted) return;
            logger.error('Error in setup:', error);
        });

        return () => {
            controller.abort();
        };
    }, [activeTourChain, tourChainIndex]);

    const startFindYourWayTour = () => {
        setChecklistOpen(true);
        clearTourChain();
        isFindYourWayTourRef.current = true;
        tourInstanceRef.current += 1;
        setTourChainIndex(0);
        setActiveTourChain(
            getChecklistTourChain(
                OnboardingEventId.EXPLORE_DASHBOARDS_FIND_YOUR_WAY,
                t,
            ),
        );
        setIsModalOnTourPageOpen(false);
        trackGuideLoaded(
            OnboardingEventId.EXPLORE_DASHBOARDS_FIND_YOUR_WAY,
            LoadType.MANUAL,
        );
    };

    const handleTourCallback = async (data: CallBackProps) => {
        const instanceAtStart = tourInstanceRef.current;

        if (data.status === STATUS.SKIPPED) {
            clearTourChain();
            return;
        }

        if (
            data.status !== STATUS.FINISHED ||
            data.lifecycle !== LIFECYCLE.COMPLETE ||
            !activeTourChain
        ) {
            return;
        }

        const tourCtx = activeTourContextRef.current;
        const currentStep = activeTourChain[tourChainIndex];

        const navUrl = currentStep.nextUrl;
        const nextIndex = tourChainIndex + 1;
        const hasMoreSteps = nextIndex < activeTourChain.length;

        if (currentStep.afterAction || currentStep.afterActions?.length) {
            const signal = tourAbortRef.current?.signal;
            const timeout = WAIT_FOR_ELEMENT_SAFETY_TIMEOUT_MS;

            if (currentStep.afterActions?.length) {
                const afterActionsController = new AbortController();
                if (signal && !currentStep.dismissOnAfterClick) {
                    signal.addEventListener(
                        'abort',
                        () => afterActionsController.abort(),
                        { once: true },
                    );
                }
                await performSequentialActions(
                    currentStep.afterActions,
                    afterActionsController.signal,
                    timeout,
                );
            } else {
                await performTourAction(
                    currentStep.afterAction,
                    signal,
                    timeout,
                );
            }

            if (currentStep.dismissOnAfterClick) {
                dismissAgentTourStep();
                return;
            }

            if (hasMoreSteps) {
                const nextStep = activeTourChain[nextIndex];
                const nextTarget = nextStep.steps[0]?.target;
                if (typeof nextTarget === 'string') {
                    if (nextStep.waitForTargetRemoval) {
                        await waitForElementRemoval(
                            nextTarget,
                            signal,
                            timeout,
                        );
                        if (tourInstanceRef.current !== instanceAtStart) {
                            return;
                        }
                    }
                    await waitForElement(nextTarget, signal, timeout);
                    if (signal?.aborted) return;
                }

                if (tourInstanceRef.current !== instanceAtStart) {
                    return;
                }
            }
        }

        if (navUrl) {
            goToUrl(navUrl);
        }

        if (hasMoreSteps) {
            setIsTourReady(false);
            setTourChainIndex(nextIndex);
        } else {
            if (currentStep.onCompleteEventId) {
                sendOnboardingEvent(currentStep.onCompleteEventId);
            }
            clearTourChain();
            if (tourCtx) {
                trackGuideCtaClicked(tourCtx.taskId, tourCtx.loadType);
            }
        }
    };

    const handleOpenChecklist = () => {
        const { firstIncompletePerSection } = getInitialTaskExpandedState(
            sparkflowInsightsData,
            checkedItems,
        );

        const firstIncompleteResult = getFirstIncompleteSectionIndex(
            sparkflowInsightsData,
            checkedItems,
        );

        let sectionIndex: number;
        let sectionOpened: string;
        let taskOpened: string;

        const lastSection = sparkflowInsightsData?.find(
            (s: ChecklistItem) => s.id === lastOpenedChecklistSectionId,
        );

        if (
            lastOpenedChecklistSectionId &&
            lastSection &&
            firstIncompletePerSection[lastOpenedChecklistSectionId]
        ) {
            sectionIndex = getSectionIndexFromSectionId(
                sparkflowInsightsData,
                lastOpenedChecklistSectionId,
            );
            sectionOpened = lastOpenedChecklistSectionId;
            taskOpened =
                firstIncompletePerSection[lastOpenedChecklistSectionId];
        } else {
            sectionIndex = firstIncompleteResult.index;
            sectionOpened = firstIncompleteResult.sectionId;
            taskOpened = firstIncompleteResult.taskId;
        }

        const {
            taskExpandedState: newExpandedState,
        } = getInitialTaskExpandedState(sparkflowInsightsData, checkedItems);
        try {
            setHeaderExpandedItemsIndexes([sectionIndex]);
            setTaskExpandedState(newExpandedState);
            setChecklistOpen(!checklistOpen);

            checklistOpenContextRef.current = {
                loadType: LoadType.MANUAL,
                sectionOpened,
                taskOpened,
                lastSectionSelected: sectionOpened,
                lastTaskSelected: taskOpened,
            };
            updateSparkflowGlobalContext({
                lastOpenedChecklistSectionId: sectionOpened,
            });
            const {
                selectedFunction,
                selectedIndustry,
            } = getSparkflowOnboardingSelections(
                isOnboardingMTServicesEnabled,
                savedRole,
            );
            checklistOpened(trialInfoProps.collectEvent, {
                selectedFunction,
                selectedIndustry,
                sectionOpened,
                taskOpened,
                loadType: LoadType.MANUAL,
            });
        } catch (error) {
            logger.error('Error opening checklist:', error);
            const errorSelections = getSparkflowOnboardingSelections(
                isOnboardingMTServicesEnabled,
                savedRole,
            );
            checklistErrorShown(trialInfoProps.collectEvent, {
                selectedFunction: errorSelections.selectedFunction,
                selectedIndustry: errorSelections.selectedIndustry,
                sectionOpened,
                sectionSelected:
                    checklistOpenContextRef.current.lastSectionSelected,
                taskOpened,
                taskSelected: checklistOpenContextRef.current.lastTaskSelected,
                loadType: LoadType.MANUAL,
                errorType:
                    error?.message ||
                    error?.toString() ||
                    'Checklist open error',
            });
        }
    };

    const handleCloseChecklist = () => {
        setChecklistOpen(false);
        setLastCompletedTaskId(null);

        const lastSectionSelected =
            checklistOpenContextRef.current.lastSectionSelected;
        if (lastSectionSelected) {
            updateSparkflowGlobalContext({
                lastOpenedChecklistSectionId: lastSectionSelected,
            });
        }

        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        checklistClosed(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            sectionOpened: checklistOpenContextRef.current.sectionOpened,
            sectionSelected:
                checklistOpenContextRef.current.lastSectionSelected,
            taskOpened: checklistOpenContextRef.current.taskOpened,
            taskSelected: checklistOpenContextRef.current.lastTaskSelected,
            loadType: checklistOpenContextRef.current.loadType,
        });
    };

    const handleFooterConnectData = () => {
        handleCloseChecklist();
        const baseUrl = getBaseUrlForTaskType(
            ChecklistTaskNavigationType.CONNECT_OWN_DATA,
            spotbuildGenResponse,
        );
        if (baseUrl) goToUrl(baseUrl);
        trackChecklistFooterCtaClicked(FooterCtaType.CONNECT_DATA);
    };

    const goToExternalUrlInNewTab = (url: string, isNewTab?: boolean) => {
        window.open(url, isNewTab ? '_blank' : '_self');
    };

    const handleChecklistTaskButtonClick = async (
        type: string,
        checkboxId: string,
    ) => {
        clearTourChain();
        if (checkboxId === OnboardingEventId.EXPLORE_INSIGHTS_USE_ENGAGE) {
            sendOnboardingEvent(OnboardingEventId.EXPLORE_INSIGHTS_USE_ENGAGE);
            goToExternalUrlInNewTab(
                EXPLORE_INSIGHTS_USE_ENGAGE_EVENT_URL,
                true,
            );
            return;
        }

        const baseUrl = getBaseUrlForTaskType(type, spotbuildGenResponse);
        const computedNavUrl =
            baseUrl && !pathname.includes(baseUrl) ? baseUrl : null;

        if (checklistOpen) {
            handleCloseChecklist();
        }

        if (
            checkboxId === OnboardingEventId.SPOTTER_CHAT_ASK_QUESTION ||
            checkboxId === OnboardingEventId.SPOTTER_CHAT_RESEARCH_MODE
        ) {
            updateSparkflowGlobalContext({
                spotterConversationResetKey:
                    (spotterConversationResetKey ?? 0) + 1,
                ...(checkboxId ===
                    OnboardingEventId.SPOTTER_CHAT_RESEARCH_MODE && {
                    analysisPreFilledQuestion: '',
                    deepResearchPreFilledQuestion: t(
                        'sparkflow.spotter.researchPreFilledQuestion',
                    ),
                }),
            });
        }

        const agentTourChain = getAgentTourChain(checkboxId, false);
        const tourChain =
            agentTourChain || getChecklistTourChain(checkboxId, t);

        if (tourChain) {
            agentTourPausedRef.current = agentTourChain ? 0 : null;
            tourInstanceRef.current += 1;
            setTourChainIndex(0);
            setActiveTourChain(tourChain);
            setIsModalOnTourPageOpen(false);
            trackGuideLoaded(checkboxId, LoadType.MANUAL);
        }
        if (
            checkboxId === OnboardingEventId.SPOTTER_VIZ_BUILD_FIRST_DASHBOARD
        ) {
            try {
                const response = await createPinboard({
                    variables: {
                        name: t('sparkflow.checklist.liveboard', {
                            name: getUserDisplayName(),
                            date: formatDate(new Date()),
                        }),
                    },
                });
                const newLiveboardId =
                    response?.data?.Pinboard__createByName?.id;
                if (newLiveboardId) {
                    goToUrl(`sparkflow/pinboard/${newLiveboardId}?mode=edit`);
                }
            } catch (err) {
                logger.error('Failed to create pinboard', err);
            }
        } else if (computedNavUrl) {
            goToUrl(computedNavUrl);
        }

        const taskSection = findSectionByTaskId(
            sparkflowInsightsData,
            checkboxId,
        );
        const {
            selectedFunction,
            selectedIndustry,
        } = getSparkflowOnboardingSelections(
            isOnboardingMTServicesEnabled,
            savedRole,
        );
        checklistTaskCtaClicked(trialInfoProps.collectEvent, {
            selectedFunction,
            selectedIndustry,
            sectionOpened: checklistOpenContextRef.current.sectionOpened,
            sectionSelected: taskSection?.id || '',
            taskOpened: checklistOpenContextRef.current.taskOpened,
            taskSelected: checkboxId,
            loadType: checklistOpenContextRef.current.loadType,
        });
    };

    const jumpToAgentTourCloseStep = () => {
        if (!activeTourChain) return false;
        const closeStepIndex = activeTourChain.findIndex(
            step => step.showOnTourClose,
        );
        if (closeStepIndex < 0) return false;
        agentTourPausedRef.current = null;
        tourInstanceRef.current += 1;
        setIsTourReady(false);
        setTourChainIndex(closeStepIndex);
        return true;
    };

    const handleTourClose = () => {
        const tourCtx = activeTourContextRef.current;
        if (!jumpToAgentTourCloseStep()) {
            clearTourChain();
        }
        if (tourCtx) {
            trackGuideClosed(tourCtx.taskId, tourCtx.loadType);
        }
    };

    const handleChecklistOutsideClick = () => {
        if (isFindYourWayTourRef.current) return;
        handleCloseChecklist();
    };

    const currentChainStep = activeTourChain?.[tourChainIndex] ?? null;

    const shouldShowTour =
        currentChainStep &&
        isTourReady &&
        (!isModalOnTourPageOpen || currentChainStep.showInsideModal);

    const onInviteTeamButtonClick = () => {
        handleCloseChecklist();
        setIsInviteModalOpen(true);
        trackChecklistFooterCtaClicked(FooterCtaType.INVITE);
    };

    const handleMilestonePrimaryAction = () => {
        const agentName = milestone.agentName;
        const status = milestone.status;

        const isSpotterOrVizStarted =
            (agentName === MilestoneAgentName.SPOTTER_CHAT ||
                agentName === MilestoneAgentName.SPOTTER_VIZ) &&
            status === MilestoneStatus.STARTED;

        const isSpotterCodeStarted =
            agentName === MilestoneAgentName.SPOTTER_CODE &&
            status === MilestoneStatus.STARTED;

        const isAgentFinished =
            (agentName === MilestoneAgentName.SPOTTER_CHAT ||
                agentName === MilestoneAgentName.SPOTTER_VIZ ||
                agentName === MilestoneAgentName.SPOTTER_CODE) &&
            status === MilestoneStatus.FINISHED;

        if (isSpotterOrVizStarted) {
            handleChecklistTaskButtonClick(
                ChecklistTaskNavigationType.LIVEBOARD,
                OnboardingEventId.EXPLORE_DASHBOARDS_VIEW_DASHBOARD,
            );
        } else if (isSpotterCodeStarted) {
            handleChecklistTaskButtonClick(
                ChecklistTaskNavigationType.SPOTTER_VIZ,
                OnboardingEventId.SPOTTER_VIZ_BUILD_FIRST_DASHBOARD,
            );
        } else if (isAgentFinished) {
            setIsInviteModalOpen(true);
        }
    };

    const handleMilestoneSecondaryAction = () => {
        const agentName = milestone.agentName;
        const status = milestone.status;

        const isAgentFinished =
            (agentName === MilestoneAgentName.SPOTTER_CHAT ||
                agentName === MilestoneAgentName.SPOTTER_VIZ ||
                agentName === MilestoneAgentName.SPOTTER_CODE) &&
            status === MilestoneStatus.FINISHED;

        const isSpotterCodeStarted =
            agentName === MilestoneAgentName.SPOTTER_CODE &&
            status === MilestoneStatus.STARTED;

        if (isAgentFinished) {
            const baseUrl = getBaseUrlForTaskType(
                ChecklistTaskNavigationType.CONNECT_OWN_DATA,
                spotbuildGenResponse,
            );
            if (baseUrl) goToUrl(baseUrl);
        } else if (isSpotterCodeStarted) {
            setIsInviteModalOpen(true);
        }
    };

    const handleMilestoneTertiaryAction = () => {
        const agentName = milestone.agentName;
        const status = milestone.status;

        const isAgentFinished =
            (agentName === MilestoneAgentName.SPOTTER_CHAT ||
                agentName === MilestoneAgentName.SPOTTER_VIZ ||
                agentName === MilestoneAgentName.SPOTTER_CODE) &&
            status === MilestoneStatus.FINISHED;

        if (isAgentFinished) {
            startFindYourWayTour();
        }
    };

    const handleMilestoneCtaAction = (actionType: MilestoneCtaActionType) => {
        const closedAgentName = milestone.agentName;
        const closedStatus = milestone.status;
        updateMilestone({
            isModalOpen: false,
            agentName: MilestoneAgentName.NONE,
            status: MilestoneStatus.NONE,
        });
        trackMilestoneModalClosed(closedAgentName, closedStatus);
        trackMilestoneCtaClicked(closedAgentName, closedStatus, actionType);
        switch (actionType) {
            case MilestoneCtaActionType.Primary:
                handleMilestonePrimaryAction();
                break;
            case MilestoneCtaActionType.Secondary:
                handleMilestoneSecondaryAction();
                break;
            case MilestoneCtaActionType.Tertiary:
                handleMilestoneTertiaryAction();
                break;
            case MilestoneCtaActionType.Dismiss:
                startFindYourWayTour();
                break;
            default:
                break;
        }
    };

    const handleMilestoneAction = (actionType: MilestoneCtaActionType) => () =>
        handleMilestoneCtaAction(actionType);

    return (
        <>
            <GlobalAlert.Banner
                type={BannerAlertType.ERROR}
                data-testid="checklist-banner-alert"
                message={bannerAlertMessage.errorMessage}
                isVisible={bannerAlertMessage.isVisible}
                onClose={() =>
                    setBannerAlertMessage({
                        isVisible: false,
                        errorMessage: '',
                    })
                }
            />
            {!hideSparkflowBanner && (
                <TrialInfoSparkflow
                    {...trialInfoProps}
                    sparkflowInsightsData={sparkflowInsightsData}
                    checkedItems={checkedItems}
                    checklistOpen={checklistOpen}
                    setChecklistOpen={setChecklistOpen}
                    onOpenChecklist={handleOpenChecklist}
                    onCloseChecklist={handleChecklistOutsideClick}
                    onChecklistTaskButtonClick={handleChecklistTaskButtonClick}
                    headerExpandedItemsIndexes={headerExpandedItemsIndexes}
                    handleSectionHeaderClick={handleSectionHeaderClick}
                    taskExpandedState={taskExpandedState}
                    handleTaskHeaderClick={handleTaskHeaderClick}
                    lastCompletedTaskId={lastCompletedTaskId}
                    onFooterConnectData={handleFooterConnectData}
                    isInviteModalOpen={isInviteModalOpen}
                    onInviteTeamButtonClick={onInviteTeamButtonClick}
                    onInviteComplete={() => setIsInviteModalOpen(false)}
                />
            )}
            <MilestoneModal
                onDismiss={handleMilestoneAction(
                    MilestoneCtaActionType.Dismiss,
                )}
                onPrimaryCtaClick={handleMilestoneAction(
                    MilestoneCtaActionType.Primary,
                )}
                onSecondaryCtaClick={handleMilestoneAction(
                    MilestoneCtaActionType.Secondary,
                )}
                onTertiaryCtaClick={handleMilestoneAction(
                    MilestoneCtaActionType.Tertiary,
                )}
            />
            {shouldShowTour && (
                <Tour
                    key={`tour-${tourInstanceRef.current}-${tourChainIndex}`}
                    steps={currentChainStep.steps}
                    run
                    continuous
                    disableOverlayClose
                    disableOverlay
                    callback={handleTourCallback}
                    onCloseButtonClick={handleTourClose}
                    doneButtonText={
                        currentChainStep.doneButtonText ?? t('gotIt')
                    }
                    nextButtonText={currentChainStep.nextButtonText}
                    showProgress={currentChainStep.showProgress}
                    zIndex={TOUR_CARD_Z_INDEX}
                    spotlightCardWidth={TOUR_CARD_WIDTH}
                />
            )}
            <GlobalAlert.Toast
                message={getCompletedToastTitle(completedSectionKey, t)}
                isVisible={isToastVisible}
                onClose={() => setIsToastVisible(false)}
                autoHide
            />
        </>
    );
};
